import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DefaultBrandLogo from '../components/ui/default-brand-logo';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { useBrandingColors } from '../hooks/useBrandingColors';

const DEFAULT_POST_LOGIN_PATH = '/recruiter/reports';

type RedirectLocation = {
	pathname?: unknown;
	search?: unknown;
	hash?: unknown;
};

type LoginLocationState = {
	from?: unknown;
};

export const resolvePostLoginPath = (state: unknown): string => {
	const from = (state as LoginLocationState | null | undefined)?.from;

	if (typeof from === 'string') {
		return resolveSafePath(from);
	}

	if (!from || typeof from !== 'object') {
		return DEFAULT_POST_LOGIN_PATH;
	}

	const location = from as RedirectLocation;
	const pathname = typeof location.pathname === 'string' ? location.pathname : '';
	const search = typeof location.search === 'string' ? location.search : '';
	const hash = typeof location.hash === 'string' ? location.hash : '';

	return resolveSafePath(`${pathname}${search}${hash}`);
};

const resolveSafePath = (path: string): string => {
	if (!path.startsWith('/') || path.startsWith('//')) {
		return DEFAULT_POST_LOGIN_PATH;
	}

	const pathname = path.split(/[?#]/, 1)[0];
	if (pathname === '/login' || pathname === '/recruiter/login') {
		return DEFAULT_POST_LOGIN_PATH;
	}
	return path;
};

const Login: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { loginAdmin } = useAuthStore();
	const colors = useBrandingColors();

	const [email, setEmail] = React.useState('');
	const [password, setPassword] = React.useState('');
	const [rememberMe, setRememberMe] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState<string>('');

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setErrorMessage('');
		try {
			await loginAdmin(email, password, rememberMe);
			navigate(resolvePostLoginPath(location.state), { replace: true });
		} catch (error: unknown) {
			console.error('Login error:', error);
			// Показать локальное сообщение над кнопкой
			setErrorMessage('Неверный email или пароль');
			// Сохранить существующее поведение (toast)
			toast.error('Ошибка входа. Проверьте email и пароль.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-[#e9eae2] via-[#f0f1ea] to-[#e5e6de] flex items-center justify-center p-8">
			<div className="w-full max-w-md">
				<div className="bg-[#f5f6f1] rounded-[24px] p-8 shadow-[0px_8px_32px_0px_rgba(0,0,0,0.08)] border border-[#e2e4e9]">
					<div className="flex flex-col items-center mb-8">
						<div className="mb-6 h-12 flex items-center justify-center">
							<DefaultBrandLogo />
						</div>
						<h1 className="text-[24px] font-['Inter_Display:Medium',_sans-serif] font-medium text-[#000000] text-center mb-2">
							Вас приветствует Релевантер
						</h1>
						<p className="text-[21px] font-['Inter:Regular',_sans-serif] text-[#525866] text-center leading-[30px]">
							Войдите, чтобы начать<br />
							автоматизированный подбор персонала
						</p>
					</div>

					<form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
						<div>
							<label htmlFor="email" data-testid="login-email-label" className="block text-[14px] font-['Inter:Medium',_sans-serif] font-medium text-[#000000] mb-2 pl-3">
								Email
							</label>
							<input
								id="email"
								data-testid="login-email"
								type="email"
								value={email}
								onChange={(e) => {
									setEmail(e.target.value);
									if (errorMessage) setErrorMessage('');
								}}
								placeholder="Введите ваш email (например: admin@company.com)"
								className="w-full px-4 py-3 bg-[#ffffff] border border-[#e2e4e9] rounded-3xl text-[16px] font-['Inter:Regular',_sans-serif] text-[#000000] placeholder:text-[#868c98] focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent transition-all"
								required
							/>
						</div>

						<div>
							<label htmlFor="password" data-testid="login-password-label" className="block text-[14px] font-['Inter:Medium',_sans-serif] font-medium text-[#000000] mb-2 pl-3">
								Пароль
							</label>
							<input
								id="password"
								data-testid="login-password"
								type="password"
								value={password}
								onChange={(e) => {
									setPassword(e.target.value);
									if (errorMessage) setErrorMessage('');
								}}
								placeholder="Введите ваш пароль (минимум 8 символов)"
								className="w-full px-4 py-3 bg-[#ffffff] border border-[#e2e4e9] rounded-3xl text-[16px] font-['Inter:Regular',_sans-serif] text-[#000000] placeholder:text-[#868c98] focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent transition-all"
								required
							/>
						</div>

						<div className="flex items-center justify-between">
							<label className="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									data-testid="login-remember-me"
									checked={rememberMe}
									onChange={(e) => setRememberMe(e.target.checked)}
									className="w-4 h-4 rounded border-2 border-[#e2e4e9] text-[#e16349] focus:ring-[#e16349] focus:ring-2"
									style={{ accentColor: colors.primary }}
								/>
								<span className="text-[14px] font-['Inter:Regular',_sans-serif] text-[#525866]">
									Запомнить меня
								</span>
							</label>

							<button
								type="button"
								data-testid="login-forgot-password"
								onClick={() => {
									// Функциональность восстановления пароля
									console.log('Forgot password clicked');
									
									// TODO: Реализовать API вызов для восстановления пароля
									// Например: forgotPassword(email)
									
									// Временная заглушка - показываем сообщение
									alert('Функция восстановления пароля будет доступна в ближайшее время. Обратитесь к администратору.');
								}}
								className="text-[14px] font-['Inter:Regular',_sans-serif] text-[#e16349] hover:underline"
							>
								Забыли пароль?
							</button>
						</div>

						{errorMessage && (
							<div data-testid="login-error" className="text-[#df1c41] text-[14px] font-['Inter:Medium',_sans-serif] text-center -mt-2">
								{errorMessage}
							</div>
						)}

						<button
							type="submit"
							data-testid="login-submit"
							disabled={isLoading}
							className="w-full bg-[#e16349] hover:bg-[#d14a31] disabled:bg-[#f0f1ea] text-white disabled:text-[#868c98] py-3 px-6 rounded-3xl text-[16px] font-['Inter:Medium',_sans-serif] font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:ring-offset-2"
						>
							{isLoading ? 'Вход...' : 'Войти'}
						</button>
					</form>
				</div>

				<div className="text-center mt-6">
					<p className="text-[12px] font-['Inter:Regular',_sans-serif] text-[#868c98]">
						© {new Date().getFullYear()} Релевантер. Все права защищены.
					</p>
				</div>
			</div>
		</div>
	);
};

export default Login;
